import React, { useCallback } from 'react'
import { Pressable, View } from 'react-native'

import { Action } from '@ambire-common/interfaces/banner'
import BatchIcon from '@common/assets/svg/BatchIcon'
import RightArrowIcon from '@common/assets/svg/RightArrowIcon'
import Banner from '@common/components/Banner'
import { useTranslation } from '@common/config/localization'
import useNavigation from '@common/hooks/useNavigation'
import useTheme from '@common/hooks/useTheme'
import { ROUTES } from '@common/modules/router/constants/common'
import {
  translateControllerMessage,
  translateI18nText
} from '@common/utils/translateControllerMessage'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useRequestsControllerState from '@web/hooks/useRequestsControllerState'

import useHomeBreakpoints from '../useHomeBreakpoints'

// Home-web (react-native-web) pending-request banner layer.
//
// The Home v2 dashboard (DashboardScreen.web.tsx) renders its own chrome and
// does NOT mount the common <DashboardBanners />, which dropped the
// "Transaction waiting to be signed on …" bar — the ONLY entry point for a
// request created with executionType: 'queue' (the "Start a batch" flow).
//
// We render the shared <Banner> directly (not the common <DashboardBanner>)
// because the open-accountOp action needs mobile-web-specific handling: in the
// browser-extension the background opens a separate action window, but the
// mobile-web SPA has no action window (getUiType().isRequestWindow === false),
// so nothing happens on "Open". Here we set the current request AND navigate to
// the sign screen route ourselves, matching the extension's behavior.
const PendingRequestsBanner = () => {
  const { t } = useTranslation()
  const { banners = [], userRequests = [] } = useRequestsControllerState()
  const { dispatch } = useBackgroundService()
  const { navigate } = useNavigation()
  const { isMobile } = useHomeBreakpoints()
  const { theme } = useTheme()

  const handleActionPress = useCallback(
    (action: Action) => {
      if (action.actionName !== 'open-accountOp') return

      dispatch({
        type: 'REQUESTS_CONTROLLER_SET_CURRENT_REQUEST_BY_ID',
        params: action.meta
      })
      // SPA has no auto-opening action window; route to the sign screen,
      // which renders the now-current request.
      navigate(ROUTES.signAccountOp)
    },
    [dispatch, navigate]
  )

  // Only the sign-related pending acc-op banners (open/reject a queued tx).
  const pending = banners.filter((b) => b.category === 'pending-to-be-signed-acc-op')

  if (!pending.length) return null

  return (
    <View style={{ gap: 8 }}>
      {pending.map((banner) => {
        const openAction = banner.actions[0]
        if (!openAction) return null

        // The server title shows the call count only when >1 ("2 Transactions"
        // vs a bare "Transaction"). Derive the count from the live request and
        // always render an explicit number, so a single-call queued tx reads
        // "1 Transaction…" instead of an ambiguous "Transaction…". Falls back to
        // the server title if the request/calls aren't resolvable on the page.
        const reqId = (openAction.meta as { requestId?: string | number })?.requestId
        const req = userRequests.find((r) => r.id === reqId)
        const callsCount =
          req?.kind === 'calls' ? req.signAccountOp?.accountOp?.calls?.length : undefined
        const network = banner.title.match(/\son\s(.+)$/)?.[1]
        // eslint-disable-next-line no-nested-ternary
        const title = callsCount
          ? network
            ? t('{{count}} Transactions waiting to be signed on {{network}}', {
                count: callsCount,
                network
              })
            : t('{{count}} Transactions waiting to be signed', { count: callsCount })
          : translateI18nText(banner.title, banner.i18n?.title)

        // The whole banner is the open affordance — tapping anywhere routes to
        // the sign screen. The trailing chevron just signals "this opens".
        return (
          <Pressable key={banner.id} onPress={() => handleActionPress(openAction)}>
            <Banner
              type="info"
              title={title}
              text={translateControllerMessage(banner.text)}
              CustomIcon={BatchIcon}
              titleFontSize={isMobile ? 13 : undefined}
              textFontSize={isMobile ? 11 : undefined}
              // Blend into the token-list card surface; force white text/icons.
              style={{
                backgroundColor: theme.homeCardBackground as string,
                borderWidth: 1,
                borderColor: theme.homeCardBorder as string
              }}
              titleColor={theme.primaryText as string}
              textColor={theme.primaryText as string}
              iconColor={theme.primaryText as string}
              renderButtons={
                <View style={{ paddingHorizontal: 4 }}>
                  <RightArrowIcon width={9} height={16} color={theme.primaryText as string} />
                </View>
              }
            />
          </Pressable>
        )
      })}
    </View>
  )
}

export default React.memo(PendingRequestsBanner)
